import { ChatbotType } from 'apis/chatbots/types/chatbots.types';
import { api } from 'app/app.api';

import {
  CreateAdaChatbotConfigurationApiArg,
  CreateAdaChatbotConfigurationApiResponse,
  CreateMimChatbotConfigurationApiArg,
  CreateMimChatbotConfigurationApiResponse,
  CreateSolvemateChatbotConfigurationApiArg,
  CreateSolvemateChatbotConfigurationApiResponse,
  DeleteChatbotConfigurationApiArg,
  DeleteMimChatbotConfigurationApiArg,
  ReadChatbotConfigurationsApiResponse,
  ReadMimChatbotConfigurationByIdApiArg,
  ReadMimChatbotConfigurationByIdApiResponse,
  ReadMimChatbotConfigurationsApiResponse,
  ReadSolvemateChatbotConfigurationByIdApiArg,
  ReadSolvemateChatbotConfigurationByIdApiResponse,
  ReadSolvemateChatbotConfigurationsApiResponse,
  UpdateMimChatbotIdentityApiArg,
  UpdateMimChatbotIdentityApiResponse,
} from './types/chatbotsApi.types';
import {
  transformCreateAdaChatbotConfigurationResponse,
  transformCreateMimChatbotConfigurationResponse,
  transformCreateSolvemateChatbotConfigurationResponse,
  transformReadChatbotConfigurationsResponse,
  transformReadMimChatbotConfigurationResponse,
  transformReadMimChatbotConfigurationsResponse,
  transformSolvemateReadChatbotConfigurationsResponse,
} from './utils/transformResponse';

export const solvemateChatbotsApi = api
  .enhanceEndpoints({
    addTagTypes: ['SolvemateChatbots'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readSolvemateChatbotById: builder.query<
        ReadSolvemateChatbotConfigurationByIdApiResponse,
        ReadSolvemateChatbotConfigurationByIdApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `chatbots/${id}`,
        }),
        providesTags: ['SolvemateChatbots'],
      }),
      readSolvemateChatbots: builder.query<
        ReadSolvemateChatbotConfigurationsApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'chatbots',
          params: {
            chatbotType: 'solvemate' as ChatbotType,
          },
        }),
        transformResponse: transformSolvemateReadChatbotConfigurationsResponse,
        providesTags: ['SolvemateChatbots'],
      }),
      createSolvemateChatbot: builder.mutation<
        CreateSolvemateChatbotConfigurationApiResponse,
        CreateSolvemateChatbotConfigurationApiArg
      >({
        query: ({ url, _type }) => ({
          version: 1,
          path: 'chatbots',
          method: 'POST',
          body: { url, _type },
        }),
        transformResponse: transformCreateSolvemateChatbotConfigurationResponse,
        invalidatesTags: ['SolvemateChatbots'],
      }),
    }),
  });

export const chatbotsApi = api
  .enhanceEndpoints({
    addTagTypes: ['Chatbots', 'SolvemateChatbots', 'MimChatbots'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readChatbots: builder.query<ReadChatbotConfigurationsApiResponse, void>({
        query: () => ({
          version: 1,
          path: 'chatbots',
          params: {
            chatbotType: ['ada', 'solvemate'] as ChatbotType[],
          },
        }),
        transformResponse: transformReadChatbotConfigurationsResponse,
        providesTags: ['Chatbots'],
      }),
      createAdaChatbot: builder.mutation<
        CreateAdaChatbotConfigurationApiResponse,
        CreateAdaChatbotConfigurationApiArg
      >({
        query: ({ name, _type }) => ({
          version: 1,
          path: 'chatbots',
          method: 'POST',
          body: { name, _type },
        }),
        transformResponse: transformCreateAdaChatbotConfigurationResponse,
        invalidatesTags: ['Chatbots'],
      }),
      deleteChatbot: builder.mutation<void, DeleteChatbotConfigurationApiArg>({
        query: ({ id }) => ({
          version: 1,
          path: `chatbots/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Chatbots', 'SolvemateChatbots', 'MimChatbots'],
      }),
    }),
  });

export const mimChatbotsApi = api
  .enhanceEndpoints({
    addTagTypes: ['MimChatbots'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readMimChatbotById: builder.query<
        ReadMimChatbotConfigurationByIdApiResponse,
        ReadMimChatbotConfigurationByIdApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `chatbots/${id}`,
        }),
        transformResponse: transformReadMimChatbotConfigurationResponse,
        providesTags: ['MimChatbots'],
      }),
      readMimChatbots: builder.query<
        ReadMimChatbotConfigurationsApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'chatbots',
          params: {
            chatbotType: 'mim' as ChatbotType,
          },
        }),
        transformResponse: transformReadMimChatbotConfigurationsResponse,
        providesTags: ['MimChatbots'],
      }),
      createMimChatbot: builder.mutation<
        CreateMimChatbotConfigurationApiResponse,
        CreateMimChatbotConfigurationApiArg
      >({
        query: ({ name, organizationId, knowledgeBases, user }) => ({
          version: 2,
          path: 'chatbots',
          method: 'POST',
          body: {
            name,
            organizationId,
            knowledgeBases,
            user,
            _type: 'mim',
          },
        }),
        transformResponse: transformCreateMimChatbotConfigurationResponse,
        invalidatesTags: ['MimChatbots'],
      }),
      updateMimChatbotIdentity: builder.mutation<
        UpdateMimChatbotIdentityApiResponse,
        UpdateMimChatbotIdentityApiArg
      >({
        query: ({ id, name, user }) => ({
          version: 1,
          path: `chatbots/${id}`,
          method: 'PUT',
          body: { name, user, _type: 'mim' },
        }),
        transformResponse: transformCreateMimChatbotConfigurationResponse,
        invalidatesTags: ['MimChatbots'],
      }),
      deleteMimChatbot: builder.mutation<
        void,
        DeleteMimChatbotConfigurationApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `chatbots/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['MimChatbots'],
      }),
    }),
  });
